import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, Mail, Loader2, ArrowLeft, AlertCircle, Timer } from 'lucide-react';
import { toast } from 'sonner';

const VerifyEmail = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { resendVerificationEmail, isAuthenticated } = useAuth();
  
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [isResending, setIsResending] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendCount, setResendCount] = useState(0);
  const [isEditingEmail, setIsEditingEmail] = useState(!email);
  
  const maxResends = 3;
  const cooldownTime = 60; // 60 seconds

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/profile', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  // Handle cooldown timer
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (resendCooldown > 0) {
      interval = setInterval(() => {
        setResendCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [resendCooldown]);

  const handleResendEmail = async () => {
    if (!email || resendCount >= maxResends || resendCooldown > 0) return;

    setIsResending(true);
    try {
      await resendVerificationEmail(email);
      setResendCount(prev => prev + 1);
      setResendCooldown(cooldownTime);
      toast.success('Verification email sent! Please check your inbox.');
    } catch (error: any) {
      toast.error(error.message || 'Failed to resend verification email. Please try again.');
    } finally {
      setIsResending(false);
    }
  };

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (email) {
      setIsEditingEmail(false);
      // Update URL to include email
      const newParams = new URLSearchParams(searchParams);
      newParams.set('email', email);
      navigate(`/verify-email?${newParams.toString()}`, { replace: true });
    }
  };

  const getResendButtonText = () => {
    if (resendCooldown > 0) {
      return `Resend in ${resendCooldown}s`;
    }
    if (resendCount >= maxResends) {
      return 'Maximum attempts reached';
    }
    return isResending ? 'Sending...' : 'Resend verification email';
  };

  const canResend = email && resendCount < maxResends && resendCooldown === 0 && !isResending;

  return (
    <div className="min-h-screen bg-linear-to-br from-primary/5 to-accent/5 flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="mx-auto w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mb-4">
            <Mail className="h-8 w-8 text-primary" />
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Check Your Email</h1>
          <p className="text-muted-foreground">
            We've sent a verification link to confirm your account
          </p>
        </div>

        {/* Main Card */}
        <Card>
          <CardHeader className="text-center pb-4">
            <div className="mx-auto w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-3">
              <CheckCircle className="h-6 w-6 text-green-600" />
            </div>
            <CardTitle className="text-xl">Account Created Successfully!</CardTitle>
            <CardDescription>
              Please check your email and click the verification link to complete your registration.
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Email Display/Edit */}
            {isEditingEmail ? (
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <label htmlFor="email" className="block text-sm font-medium mb-2">
                    Email Address
                  </label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Enter your email address"
                    required
                    autoFocus
                  />
                </div>
                <Button type="submit" className="w-full" disabled={!email}>
                  Continue
                </Button>
              </form>
            ) : (
              <div className="space-y-4">
                <div className="p-4 bg-muted/50 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium">Email sent to:</p>
                      <p className="text-lg font-mono break-all">{email}</p>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setIsEditingEmail(true)}
                    >
                      Edit
                    </Button>
                  </div>
                </div>

                {/* Resend Button */}
                <Button
                  onClick={handleResendEmail}
                  disabled={!canResend}
                  className="w-full"
                  variant={resendCount >= maxResends ? "destructive" : "outline-solid"}
                >
                  {isResending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                  {resendCooldown > 0 && <Timer className="h-4 w-4 mr-2" />}
                  {getResendButtonText()}
                </Button>

                {/* Resend Status */}
                {resendCount > 0 && (
                  <div className="text-center">
                    <p className="text-sm text-muted-foreground">
                      Emails sent: {resendCount}/{maxResends}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Alert for maximum attempts */}
            {resendCount >= maxResends && (
              <Alert>
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>
                  You've reached the maximum number of resend attempts. If you're still having trouble, please contact support or try again later.
                </AlertDescription>
              </Alert>
            )}

            {/* Next Steps */}
            <div className="space-y-3">
              <h3 className="font-medium">What happens next?</h3>
              <div className="space-y-2 text-sm text-muted-foreground">
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 shrink-0" />
                  <p>Check your email inbox for a message from LightsHunt</p>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 shrink-0" />
                  <p>Click the verification link in the email</p>
                </div>
                <div className="flex items-start space-x-2">
                  <div className="w-2 h-2 bg-primary rounded-full mt-2 shrink-0" />
                  <p>You'll be automatically signed in and can start using your account</p>
                </div>
              </div>
            </div>

            {/* Troubleshooting */}
            <Accordion type="single" collapsible>
              <AccordionItem value="troubleshooting">
                <AccordionTrigger className="text-sm">
                  Not receiving the email?
                </AccordionTrigger>
                <AccordionContent className="space-y-4">
                  <div className="space-y-3 text-sm">
                    <div>
                      <h4 className="font-medium text-foreground mb-2">Check these locations:</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Your spam or junk mail folder</li>
                        <li>• Your promotions or updates folder (Gmail)</li>
                        <li>• Search for "noreply@lightshunt.com"</li>
                      </ul>
                    </div>
                    
                    <div>
                      <h4 className="font-medium text-foreground mb-2">Still not there?</h4>
                      <ul className="space-y-1 text-muted-foreground">
                        <li>• Wait a few minutes - emails can be delayed</li>
                        <li>• Add noreply@lightshunt.com to your contacts</li>
                        <li>• Check that you entered the correct email address</li>
                        <li>• Try using the resend button above</li>
                      </ul>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/* Navigation */}
        <div className="flex justify-center space-x-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/auth')}
            className="text-muted-foreground"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back to Sign In
          </Button>
        </div>

        {/* Footer */}
        <div className="text-center text-xs text-muted-foreground">
          <p>
            Need help? Contact us at{' '}
            <a href="mailto:support@lightshunt.com" className="text-primary hover:underline">
              support@lightshunt.com
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default VerifyEmail;